import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { SkeletonCardFields } from 'components/Loading';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  Box,
  Grid,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import 'bootstrap/dist/css/bootstrap.css';
import InfoIcon from '@material-ui/icons/Info';
import { CloseIcon } from 'components';
import {
  SurveyAIGenerator,
  isSurveyAiPreviewEnabled
} from 'features/surveyAi';
import { actions as surveyAiActions } from 'store/surveyAi';
import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import { Model } from "survey-core";
import "survey-core/defaultV2.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import { setLicenseKey } from "survey-core";
import { getEnvVar } from "helpers";
import "survey-creator-core/survey-creator-core.i18n";

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  aiAssistButton: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
    background: theme.palette.ai.button.background,
    color: '#fff',
    '&:hover': {
      background: theme.palette.ai.button.background,
      opacity: 0.9
    },
    '& img': {
      filter: 'brightness(0) invert(1)'
    }
  },
  aiContextScope: {
    marginTop: theme.spacing(2),
    maxWidth: '22rem'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
}));

const AI_CONTEXT_SCOPE = {
  SURVEY: 'survey',
  PAGE: 'page',
  QUESTION: 'question'
};

const AI_CONTEXT_TARGET_SURVEY = 'survey';

const safeParseJson = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

const normalizeNameToken = value => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_\s-]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalizedValue;
};

const getUniqueName = (rawBaseName, usedNames, fallbackBaseName) => {
  const baseName = normalizeNameToken(rawBaseName) || fallbackBaseName;
  let candidate = baseName;
  let suffix = 1;

  while (usedNames.has(candidate)) {
    suffix += 1;
    candidate = `${baseName}_${suffix}`;
  }

  usedNames.add(candidate);
  return candidate;
};

const normalizeSurveyForAiContext = surveyJson => {
  const clonedSurvey =
    surveyJson && typeof surveyJson === 'object'
      ? JSON.parse(JSON.stringify(surveyJson))
      : surveyJson;

  const pages = Array.isArray(clonedSurvey?.pages) ? clonedSurvey.pages : null;
  if (!pages) {
    return clonedSurvey;
  }

  const usedPageNames = new Set();
  const usedQuestionNames = new Set();

  pages.forEach((page, pageIndex) => {
    if (!page || typeof page !== 'object') {
      return;
    }

    const pageName = getUniqueName(
      page?.name || page?.title,
      usedPageNames,
      `page_${pageIndex + 1}`
    );
    page.name = pageName;

    const elements = Array.isArray(page.elements) ? page.elements : [];
    elements.forEach((question, questionIndex) => {
      if (!question || typeof question !== 'object') {
        return;
      }

      const questionName = getUniqueName(
        question?.name || question?.title,
        usedQuestionNames,
        `question_${pageIndex + 1}_${questionIndex + 1}`
      );
      question.name = questionName;
    });
  });

  return clonedSurvey;
};

const Design = props => {
  const {
    className,
    staticContext,
    survey,
    back,
    showNav,
    next,
    currentBusinessContextId,
    ...rest
  } = props;
  const {
    thisSurvey,
    handleDesignChange,
    handleChange,
    updateEditorTheme,
    handleSurveyPermissions,
    shouldShowError,
    isSurveyLoading,
    goBack,
    goNext,
    navBackOnError,
    updateCurrentErrorValidationSegment,
    currentContext,
    saveSurvey,
    closeSurvey,
    isTemplateReadOnly
  } = survey;
  const dispatch = useDispatch();
  const classes = useStyles();

  const currentLanguage = useSelector(store => store.settings.currentLanguage);
  const selectedTenantId = useSelector(store => store.user.selectedTenant?.tenantId);
  const isSurveyAiPreviewFeatureEnabled = isSurveyAiPreviewEnabled(
    selectedTenantId ?? window.localStorage.getItem('GCM-tenantId')
  );
  
  const [creatorInstance, setCreatorInstance] = React.useState(null);
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [generatedJson, setGeneratedJson] = React.useState(null);
  const [previewCreator, setPreviewCreator] = React.useState(null);
  const [aiContextScope, setAiContextScope] = React.useState(AI_CONTEXT_TARGET_SURVEY);
  const [surveyAiHistory, setSurveyAiHistory] = React.useState([]);
  const [surveyAiHistoryIndex, setSurveyAiHistoryIndex] = React.useState(-1);

  const cloneJson = value => {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return value;
    }
  };

  const getScopeFromCreatorSelection = (creator, selectedElementOverride) => {
    if (!creator) {
      return AI_CONTEXT_TARGET_SURVEY;
    }

    const creatorSurvey = creator.survey;
    const selectedElement = selectedElementOverride || creator.selectedElement;
    const creatorPages = Array.isArray(creatorSurvey?.pages) ? creatorSurvey.pages : [];

    if (!selectedElement || creatorPages.length === 0) {
      return AI_CONTEXT_TARGET_SURVEY;
    }

    const selectedType =
      typeof selectedElement?.getType === 'function'
        ? selectedElement.getType()
        : selectedElement?.type;

    if (selectedType === 'survey' || selectedElement === creatorSurvey) {
      return AI_CONTEXT_TARGET_SURVEY;
    }

    const pageIndexByRef = creatorPages.findIndex(page => page === selectedElement);
    if (pageIndexByRef > -1) {
      return `page:${pageIndexByRef}`;
    }

    for (let pageIndex = 0; pageIndex < creatorPages.length; pageIndex += 1) {
      const elements = Array.isArray(creatorPages[pageIndex]?.elements)
        ? creatorPages[pageIndex].elements
        : [];

      const questionIndex = elements.findIndex(element => {
        if (element === selectedElement) {
          return true;
        }
        return !!selectedElement?.name && element?.name === selectedElement.name;
      });

      if (questionIndex > -1) {
        return `question:${pageIndex}:${questionIndex}`;
      }
    }

    if (selectedElement?.page) {
      const pageIndex = creatorPages.findIndex(page => page === selectedElement.page);
      if (pageIndex > -1) {
        return `page:${pageIndex}`;
      }
    }

    return AI_CONTEXT_TARGET_SURVEY;
  };

  const applyScopeToCreatorSelection = (creator, scopeValue) => {
    if (!creator || !scopeValue) {
      return;
    }

    const creatorSurvey = creator.survey;
    const creatorPages = Array.isArray(creatorSurvey?.pages) ? creatorSurvey.pages : [];

    if (scopeValue === AI_CONTEXT_TARGET_SURVEY) {
      if (creator.selectedElement !== creatorSurvey) {
        creator.selectedElement = creatorSurvey;
      }
      return;
    }

    if (scopeValue.startsWith('page:')) {
      const [, pageIndexRaw] = scopeValue.split(':');
      const pageIndex = Number(pageIndexRaw);
      const targetPage = creatorPages[pageIndex];
      if (targetPage && creator.selectedElement !== targetPage) {
        creator.selectedElement = targetPage;
      }
      return;
    }

    if (scopeValue.startsWith('question:')) {
      const [, pageIndexRaw, questionIndexRaw] = scopeValue.split(':');
      const pageIndex = Number(pageIndexRaw);
      const questionIndex = Number(questionIndexRaw);
      const targetPage = creatorPages[pageIndex];
      const targetQuestion = Array.isArray(targetPage?.elements)
        ? targetPage.elements[questionIndex]
        : null;

      if (targetQuestion && creator.selectedElement !== targetQuestion) {
        creator.selectedElement = targetQuestion;
      }
    }
  };

  const livePreviewJson =
    (previewCreator && safeParseJson(previewCreator.text)) || null;
  const aiContextSurveyJson =
    normalizeSurveyForAiContext(livePreviewJson || generatedJson || { pages: [] });

  const aiContextScopeOptions = useMemo(() => {
    // Prefer the plain-JSON source (generatedJson) so we have the full data;
    // fall back to the live SurveyJS survey model pages when the dialog has
    // a creator but generatedJson hasn't been set yet.
    const rawPages =
      Array.isArray(livePreviewJson?.pages)
        ? livePreviewJson.pages
        : Array.isArray(generatedJson?.pages)
        ? generatedJson.pages
        : Array.isArray(previewCreator?.survey?.pages)
        ? previewCreator.survey.pages
        : [];
    const pages = Array.isArray(rawPages) ? rawPages : [];
    const options = [
      {
        id: 'ai-context-survey-label',
        value: 'ai-context-survey-label',
        displayText: 'Survey',
        isDisabled: true,
        className: 'level1'
      },
      {
        id: AI_CONTEXT_TARGET_SURVEY,
        value: AI_CONTEXT_TARGET_SURVEY,
        displayText: 'Entire Survey',
        className: 'level4',
        customStyles: {
          paddingLeft: '24px'
        }
      }
    ];

    if (pages.length > 0) {
      options.push({
        id: 'ai-context-pages-label',
        value: 'ai-context-pages-label',
        displayText: 'Pages',
        isDisabled: true,
        className: 'level1'
      });
    }

    pages.forEach((page, pageIndex) => {
      const pageTitle = page?.title || page?.name || `Page ${pageIndex + 1}`;
      options.push({
        id: `page-${pageIndex}`,
        value: `page:${pageIndex}`,
        displayText: pageTitle,
        className: 'level4',
        customStyles: {
          paddingLeft: '32px'
        }
      });

      const elements = Array.isArray(page?.elements) ? page.elements : [];
      if (elements.length > 0) {
        options.push({
          id: `ai-context-questions-label-${pageIndex}`,
          value: `ai-context-questions-label-${pageIndex}`,
          displayText: 'Questions',
          isDisabled: true,
          className: 'level1',
          customStyles: {
            paddingLeft: '40px'
          }
        });
      }
      elements.forEach((question, questionIndex) => {
        const questionTitle = question?.title || question?.name || `Question ${questionIndex + 1}`;
        options.push({
          id: `question-${pageIndex}-${questionIndex}`,
          value: `question:${pageIndex}:${questionIndex}`,
          displayText: questionTitle,
          className: 'level4',
          customStyles: {
            paddingLeft: '48px'
          }
        });
      });
    });

    return {
      list: options,
      keyId: 'id',
      valueId: 'value',
      displayValueId: 'displayText'
    };
  }, [generatedJson, livePreviewJson, previewCreator]);

  useEffect(() => {
    // Only reset scope when the AI dialog is open. When closed, aiContextScopeOptions
    // are built from generatedJson (null) which has no question entries — so any
    // element selected in the main designer would be incorrectly wiped to 'survey'.
    if (!previewVisible) return;
    const contextOptionExists = aiContextScopeOptions.list.some(option => option.value === aiContextScope);
    if (!contextOptionExists) {
      setAiContextScope(AI_CONTEXT_TARGET_SURVEY);
    }
  }, [aiContextScopeOptions, aiContextScope, previewVisible]);

  useEffect(() => {
    if (!previewVisible || !previewCreator) {
      return;
    }
    applyScopeToCreatorSelection(previewCreator, aiContextScope);
  }, [aiContextScope, previewCreator, previewVisible]);

  const handleAccept = () => {
    // Use the live creator text as the source of truth so that any manual
    // edits made directly in the preview designer are included, even if the
    // generatedJson React state hasn't propagated yet.
    const liveJson = (previewCreator && safeParseJson(previewCreator.text)) || generatedJson;
    const normalizedLiveJson = normalizeSurveyForAiContext(liveJson);
    if (creatorInstance && liveJson) {
      creatorInstance.text = JSON.stringify(normalizedLiveJson);
    }
    const modifiedSurvey = { ...thisSurvey, details: JSON.stringify(normalizedLiveJson) };
    handleDesignChange(modifiedSurvey);
    setPreviewVisible(false);
    setGeneratedJson(null);
    setPreviewCreator(null);
    setSurveyAiHistory([]);
    setSurveyAiHistoryIndex(-1);
  };

  const handleReject = () => {
    setPreviewVisible(false);
    setGeneratedJson(null);
    setPreviewCreator(null);
    setSurveyAiHistory([]);
    setSurveyAiHistoryIndex(-1);
  };

  const validateSurveyJson = surveyJson => {
    let isValid = true;
    try {
      const surveyModel = new Model();
      surveyModel.fromJSON(surveyJson, { validatePropertyValues: true });
      const jsonErrors = Array.isArray(surveyModel.jsonErrors) ? surveyModel.jsonErrors : [];
      isValid = jsonErrors.length === 0;
    } catch (error) {
      isValid = false;
    }
    return isValid;
  };

  const updatePreviewCreator = (surveyJson) => {
    try {
      const normalizedSurveyJson = normalizeSurveyForAiContext(surveyJson);
      setGeneratedJson(normalizedSurveyJson);
      
      if (previewCreator) {
          previewCreator.text = JSON.stringify(normalizedSurveyJson);
      } else {
          const pc = new SurveyCreator({
              showLogicTab: false,
              showJSONEditorTab: false,
              showThemeTab: false,
              showTranslationTab: false,
              showDesignerTab: true,
              showPreviewTab: true,
              showTestSurveyTab: true
          });
          pc.text = JSON.stringify(normalizedSurveyJson);
          pc.locale = currentLanguage?.surveyLocale || 'en';
          pc.onSelectedElementChanged.add((_, options) => {
            const preselectedScope = getScopeFromCreatorSelection(
              pc,
              options?.newSelectedElement
            );
            setAiContextScope(preselectedScope);
          });
          // Keep generatedJson in sync when the user manually edits in the
          // preview designer so that AI context and Accept & Apply always
          // see the current state of the survey.
          pc.onModified.add(() => {
            const updatedJson = safeParseJson(pc.text);
            if (updatedJson) {
              setGeneratedJson(normalizeSurveyForAiContext(updatedJson));
            }
          });
          if (thisSurvey.editorTheme || thisSurvey.surveyTheme) {
                pc.theme = creatorInstance?.theme || {};
          }
          setPreviewCreator(pc);
      }
    } catch (error) {
      throw new Error(`Failed to apply generated survey JSON: ${error?.message || 'Unknown error'}`);
    }
  };

  const getSelectedElementForAiRequest = surveyJson => {
    if (aiContextScope === AI_CONTEXT_TARGET_SURVEY) {
      return null;
    }

    const pages = Array.isArray(surveyJson?.pages) ? surveyJson.pages : [];

    if (aiContextScope.startsWith('question:')) {
      const [, pageIndexRaw, questionIndexRaw] = aiContextScope.split(':');
      const pageIndex = Number(pageIndexRaw);
      const questionIndex = Number(questionIndexRaw);
      const page = pages[pageIndex];
      const question = Array.isArray(page?.elements) ? page.elements[questionIndex] : null;

      if (question) {
        return (
          question?.name ||
          question?.title ||
          `question_${pageIndex + 1}_${questionIndex + 1}`
        );
      }
    }

    if (aiContextScope.startsWith('page:')) {
      const [, pageIndexRaw] = aiContextScope.split(':');
      const pageIndex = Number(pageIndexRaw);
      const page = pages[pageIndex];

      if (page) {
        return page?.name || page?.title || `page_${pageIndex + 1}`;
      }
    }

    return null;
  };

  const requestAiSurveyGeneration = async ({ text, messageHistory, query, currentSurveyJSON, selectedElement, signal }) => {
    return new Promise((resolve, reject) => {
      if (signal) {
        signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
      }
      dispatch(
        surveyAiActions.querySurveyAi({
          text,
          messageHistory,
          query,
          currentSurveyJSON,
          selectedElement,
          signal,
          resolve,
          reject
        })
      );
    });
  };

  const handleAiSurveyGenerated = (surveyJson, meta = {}) => {
    if (!surveyJson || typeof surveyJson !== 'object') {
      throw new Error('AI response did not produce a valid survey JSON object');
    }

    const isValid = validateSurveyJson(surveyJson);
    if (!isValid) {
      const notificationMessage = 'Generated JSON schema contains errors. Please refine and try again.';
      if (previewCreator?.notify) {
        previewCreator.notify(notificationMessage, 'error');
      } else if (creatorInstance?.notify) {
        creatorInstance.notify(notificationMessage, 'error');
      }
      return;
    }

    try {
      updatePreviewCreator(surveyJson);
      setSurveyAiHistory(prev => {
        const boundedHistory = surveyAiHistoryIndex >= 0
          ? prev.slice(0, surveyAiHistoryIndex + 1)
          : prev;
        return [...boundedHistory, cloneJson(surveyJson)];
      });
      setSurveyAiHistoryIndex(prev => prev + 1);
    } catch (error) {
      if (previewCreator?.notify) {
        previewCreator.notify(error.message, 'error');
      } else if (creatorInstance?.notify) {
        creatorInstance.notify(error.message, 'error');
      }
      return;
    }

    if (meta?.comment && previewCreator?.notify) {
      previewCreator.notify(meta.comment, 'info');
    }
  };

  const handleUndoAiSurveyChange = (targetScope) => {
    if (surveyAiHistoryIndex <= 0) {
      return;
    }

    const previousIndex = surveyAiHistoryIndex - 1;
    const previousSurveyJson = surveyAiHistory[previousIndex];
    if (!previousSurveyJson) {
      return;
    }

    try {
      updatePreviewCreator(cloneJson(previousSurveyJson));
      setSurveyAiHistoryIndex(previousIndex);
      
      if (targetScope) {
        setAiContextScope(targetScope);
      }

      if (previewCreator?.notify) {
        previewCreator.notify('Reverted to previous AI version.', 'info');
      }
    } catch (error) {
      if (previewCreator?.notify) {
        previewCreator.notify(error.message, 'error');
      } else if (creatorInstance?.notify) {
        creatorInstance.notify(error.message, 'error');
      }
    }
  };

  const handleAiContextScopeChange = event => {
    setAiContextScope(event.target.value);
  };

  const handleOpenAIPreview = () => {
    if (!isSurveyAiPreviewFeatureEnabled) {
      return;
    }

    let initialJson = { pages: [] };

    // Prefer the live creator text so that unsaved edits and the currently
    // selected element are fully reflected when the dialog opens.
    if (creatorInstance) {
      const liveJson = safeParseJson(creatorInstance.text);
      if (liveJson) {
        initialJson = liveJson;
      } else if (thisSurvey?.details) {
        const savedJson = safeParseJson(thisSurvey.details);
        if (savedJson) initialJson = savedJson;
      }
      const preselectedScope = getScopeFromCreatorSelection(creatorInstance);
      setAiContextScope(preselectedScope);
    } else if (thisSurvey?.details) {
      const savedJson = safeParseJson(thisSurvey.details);
      if (savedJson) initialJson = savedJson;
    }

    setSurveyAiHistory([cloneJson(initialJson)]);
    setSurveyAiHistoryIndex(0);
    updatePreviewCreator(initialJson);
    setPreviewVisible(true);
  };

  useEffect(() => {
    setLicenseKey(getEnvVar('ADM_SURVEY_LICENSE_KEY'));
  }, []);

  useEffect(() => {
    updateCurrentErrorValidationSegment('design');
    navBackOnError(back);
  }, [thisSurvey, updateCurrentErrorValidationSegment]);

  function getSurveyPermission() {
    return (
      (currentBusinessContextId === thisSurvey.businessContextId ||
        !thisSurvey.id) &&
      handleSurveyPermissions('Rguest_writeSurvey')
    );
  }
  const isSurveyReadOnly = () => {
    return !getSurveyPermission() || !!thisSurvey.locked;
  };
  // Show AI Assist only when the survey is fully editable:
  // - feature must be enabled for the tenant
  // - survey must not be locked
  // - user must have write permission in the current business context
  // - survey must not be a read-only template (global / tenant-default viewed from another context)
  const canShowSurveyAiFab =
    isSurveyAiPreviewFeatureEnabled &&
    !thisSurvey.locked &&
    !isSurveyReadOnly() &&
    !isTemplateReadOnly();
  
  useEffect(() => {
    if (thisSurvey.schemaIdentifier) {
      //options to add properties in survey creator
      let creatorOptions = {
        showLogicTab: true,
        showJSONEditorTab: false,
        showPropertyGrid: 'right',
        isAutoSave: true,
        autoSaveDelay: '3000s',
        haveCommercialLicense: true,
        showThemeTab: !isSurveyReadOnly(),
        showToolbox: !isSurveyReadOnly(),
        readOnly: isSurveyReadOnly()
      };
      const creator = new SurveyCreator(creatorOptions);
      setCreatorInstance(creator);
      creator.locale = currentLanguage?.surveyLocale || 'en';
      creator.text = thisSurvey.details || '';
      creator.toolbox.removeItem('ranking');
      creator.toolbox.removeItem('boolean');
      creator.onSelectedElementChanged.add((_, options) => {
        const preselectedScope = getScopeFromCreatorSelection(
          creator,
          options?.newSelectedElement
        );
        setAiContextScope(preselectedScope);
      });
      creator.saveSurveyFunc = function(saveNo, callback) {
        // Parse and normalize the survey details
        const creatorText = creator.text;
        let surveyDetails;
        try {
          surveyDetails = JSON.parse(creatorText);
        } catch (e) {
          // If parsing fails, just use handleChange
          handleChange({ target: { name: 'details', value: creatorText } });
          !!callback && callback(saveNo, true);
          return;
        }
        
        // Normalize rateValues and sync name with title
        if (surveyDetails?.pages) {
          surveyDetails.pages = surveyDetails.pages.map(page => {
            const elements = page.elements || [];
            const modifiedElements = elements.map(element => {
              let modifiedElement = { ...element, ...(element.title && { name: element.title }) };
              // Modify rateValues if the element has it
              if (element.rateValues) {
                modifiedElement.rateValues = element.rateValues.map(rateValue => {
                  if (typeof rateValue === 'object' && rateValue !== null) {
                    return {
                      value: !isNaN(Number(rateValue.text)) ? Number(rateValue.text) : rateValue.text,
                      text: rateValue.text
                    };
                  } else {
                    return {
                      value: rateValue,
                      text: rateValue
                    };
                  }
                });
                // Find the maximum value in the text keys of the rateValues array, ignoring non-numeric values
                const numericRateValues = modifiedElement.rateValues
                  .map(rv => Number(rv.text))
                  .filter(value => !isNaN(value));
                if (numericRateValues.length > 0) {
                  modifiedElement.rateMax = Math.max(...numericRateValues);
                }
              }
              return modifiedElement;
            });
            return { ...page, elements: modifiedElements };
          });
        }
        
        // Create modified survey with normalized details
        const modifiedSurvey = { ...thisSurvey, details: JSON.stringify(surveyDetails) };
        
        // Call handleDesignChange with the normalized survey
        handleDesignChange(modifiedSurvey);
        
        !!callback && callback(saveNo, true);
      };
      if (!thisSurvey.editorTheme && thisSurvey.surveyTheme) {
        creator.theme = {
          ...creator.theme,
          isPanelless: true,
          cssVariables: {
            ...creator.theme.cssVariables,
            "--sjs-font-family": `${thisSurvey.surveyTheme?.fontFamily || 'Times New Roman,serif'}`,
            "--sjs-font-size": `${thisSurvey.surveyTheme?.fontSize || 14}px`,
            "--sjs-general-backcolor-dim": `${thisSurvey.surveyTheme?.secondaryColor || '#ffffff'}`,
            "--sjs-header-backcolor": `${thisSurvey.surveyTheme?.secondaryColor || '#ffffff'}`,
            "--sjs-primary-backcolor": `${thisSurvey.surveyTheme?.primaryColor || '#43a047'}`,
            "--sjs-font-headertitle-color": `${thisSurvey.surveyTheme?.primaryColor || '#43a047'}`,
            "--sjs-font-headerdescription-color": `${thisSurvey.surveyTheme?.primaryColor || '#43a047'}`
          }
        };
        updateEditorTheme(creator.theme);
      } else {
        creator.theme = thisSurvey.editorTheme || {};
      }
      creator.saveThemeFunc = function(saveNo, callback) {
        updateEditorTheme(creator.theme);
      };
    }
  }, [thisSurvey.schemaIdentifier, isSurveyLoading]);

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields />
        </Grid>
      </CardContent>
    );
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <div className={classes.headerContainer}>
            <div className={classes.title}>
              <CardHeader
                subheader="* indicates a required field"
                title="Survey Design"
                titleTypographyProps={{ variant: 'h1' }}
              />
              {isTemplateReadOnly() && (
                <Tooltip
                  title={
                    "You do not have access to edit this survey. Tenant Default surveys can be edited only with 'ALL' property."
                  }>
                  <InfoIcon size="small" style={{ marginTop: '26px' }} />
                </Tooltip>
              )}
            </div>
            <CloseIcon onClickClose={closeSurvey} />
          </div>
          {isSurveyLoading && <SkeletonCardContent />}
          {!isSurveyLoading && (
            <CardContent>
              <div id="creatorElement">
                {creatorInstance && <SurveyCreatorComponent creator={creatorInstance} />}
              </div>
              {shouldShowError(`details`) && (
                <Typography
                  className={classes.templateButtonError}
                  variant="caption"
                  display="block">
                  *Please add details to the Survey.
                </Typography>
              )}
            </CardContent>
          )}
          {(back || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={isSurveyLoading}
                  link={back}
                  onClick={goBack}>
                  Back
                </Button>
                <Typography></Typography>
                <Box flexGrow={1}>&nbsp;</Box>
                <Box display="flex" alignItems="center">
                  {canShowSurveyAiFab && (
                    <Tooltip title="Preview & Refine with AI" placement="top">
                      <Button
                        disabled={isSurveyLoading}
                        variant="contained"
                        startIcon={<img src={`${process.env.PUBLIC_URL}/images/ai_sparkle_24px.svg`} alt="" width={24} height={24} />}
                        className={classes.aiAssistButton}
                        onClick={handleOpenAIPreview}>
                        AI Assistant
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isSurveyLoading}
                    link={next}
                    onClick={goNext}>
                    Next
                  </Button>
                </Box>
              </CardActions>
            </>
          )}
        </form>
      </Card>
      {isSurveyAiPreviewFeatureEnabled && (
      <>
      <Dialog open={previewVisible} onClose={handleReject} fullWidth maxWidth="xl">
        <DialogContent dividers style={{ height: '80vh', padding: 0 }}>
          <Grid container style={{ height: '100%' }}>
            <Grid item xs={8} style={{ height: '100%', borderRight: '1px solid #e0e0e0', overflow: 'hidden' }}>
              {previewCreator && (
                <div style={{ height: '100%', overflow: 'auto' }}>
                  <SurveyCreatorComponent creator={previewCreator} />
                </div>
              )}
            </Grid>
            <Grid item xs={4} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box flexGrow={1} style={{ overflow: 'hidden' }}>
                  <SurveyAIGenerator
                    contextData={aiContextSurveyJson}
                    selectedElement={getSelectedElementForAiRequest(aiContextSurveyJson)}
                    requestAi={requestAiSurveyGeneration}
                    canUndoChange={surveyAiHistoryIndex > 0}
                    onUndoLastChange={handleUndoAiSurveyChange}
                    onSuccess={handleAiSurveyGenerated}
                    parseJson={true}
                    handleAccept={handleAccept}
                    handleReject={handleReject}
                    aiContextScope={aiContextScope}
                    aiContextScopeOptions={aiContextScopeOptions}
                    onAiContextScopeChange={handleAiContextScopeChange}
                  />
                </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default Design;
